import React, { useState, useCallback, useEffect, useRef } from "react";
import useApiEp from "../../hooks/useApiEp";
import useExportExcel from "../../hooks/useExportExcel";
import DataTable from "../../widgets/DataTable";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { getErrorResponse } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

// ─── constants ───────────────────────────────────────────────────────────────

const TYPE_OPTIONS = ["credit", "debit"];
const CAT_OPTIONS = ["usage", "recharge", "refund"];
const LIMIT_OPTIONS = [10, 25, 50, 100];
const EMPTY_FILTERS = {
  type: "",
  category: "",
  startDate: "",
  endDate: "",
  customerId: "",
};

const BASE = "/rcs/rcs-transaction-history";

// ─── helpers ─────────────────────────────────────────────────────────────────

const typeChip = (val) =>
  val === "credit" ? (
    <Chip label="Credit" color="success" size="small" variant="outlined" />
  ) : (
    <Chip label="Debit" color="error" size="small" variant="outlined" />
  );

const fmtDate = (val) =>
  val
    ? new Date(val).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
    : "—";

const truncate = (str, n = 28) =>
  str && str.length > n ? (
    <Tooltip title={str}>
      <span style={{ cursor: "default" }}>{str.slice(0, n) + "…"}</span>
    </Tooltip>
  ) : (
    str ?? "—"
  );

const COLUMNS = [
  { id: "type", label: "Type", render: typeChip },
  { id: "category", label: "Category", render: (v) => v ?? "—" },
  { id: "amount", label: "Amount (₹)", align: "right" },
  { id: "recipient", label: "Recipient" },
  { id: "campaignName", label: "Campaign", render: (v) => v ?? "—" },
  { id: "messageId", label: "Message ID", render: (v) => truncate(v) },
  {
    id: "createdOn",
    label: "Date",
    render: (v, row) => fmtDate(v ?? row.updatedOn),
  },
];

const toExcelRow = (row) => ({
  Type: row.type ?? "",
  Category: row.category ?? "",
  "Amount (₹)": row.amount ?? "",
  Recipient: row.recipient ?? "",
  Campaign: row.campaignName ?? "",
  "Message ID": row.messageId ?? "",
  Date: fmtDate(row.createdOn ?? row.updatedOn),
});

// ─── component ───────────────────────────────────────────────────────────────

const UsageHistoryRcs = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [snack, setSnack] = useState(null);

  // ── debounce: auto-apply filters 300 ms after last change ─────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setApplied(filters);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  // ── customer list ─────────────────────────────────────────────────────────
  const { data: customerData, isLoading: isCustomerLoading } =
    useApiEp(`/api/customer-ids`);
  const customerIds = customerData ?? [];

  // ── paginated data ────────────────────────────────────────────────────────
  const buildUrl = useCallback(() => {
    const params = new URLSearchParams({ page, limit });
    Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
    return `${BASE}?${params.toString()}`;
  }, [page, limit, applied]);

  const { data, isLoading, isError, error } = useApiEp(buildUrl());

  // ── export ────────────────────────────────────────────────────────────────
  const buildExportParams = useCallback(() => {
    const params = {};
    Object.entries(applied).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    return params;
  }, [applied]);

  const { exportToExcel, exporting } = useExportExcel({
    url: `${API_ENDPOINT}${BASE}/export`,
    buildParams: buildExportParams,
    mapRow: toExcelRow,
    filename: "rcs_transaction_history",
  });

  // Wrap export in try/catch so a thrown error never crashes the page
  const handleExport = async () => {
    try {
      const result = await exportToExcel();
      // Support hooks that resolve with { error } or just throw
      if (result?.error) {
        setSnack({ severity: "error", message: result.error });
      }
    } catch (err) {
      setSnack({
        severity: "error",
        message: err?.message ?? "Export failed. Please try again.",
      });
    }
  };

  // ── filter helpers ────────────────────────────────────────────────────────
  const handleFilterChange = (field) => (value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    // applied will sync via the debounce effect
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mt: 3 }}>
      {/* Filter bar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Filters
          </Typography>

          <Button
            variant="outlined"
            size="small"
            startIcon={
              exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />
            }
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export Excel"}
          </Button>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end">
          {/* Customer ID — Autocomplete */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={customerIds}
              value={filters.customerId || null}
              onChange={(_, newValue) =>
                handleFilterChange("customerId")(newValue ?? "")
              }
              loading={isCustomerLoading}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer ID"
                  placeholder="Search…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isCustomerLoading && (
                          <CircularProgress color="inherit" size={14} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Type */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Type"
              value={filters.type}
              onChange={(e) => handleFilterChange("type")(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {TYPE_OPTIONS.map((t) => (
                <MenuItem
                  key={t}
                  value={t}
                  sx={{ textTransform: "capitalize" }}
                >
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={filters.category}
              onChange={(e) => handleFilterChange("category")(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {CAT_OPTIONS.map((c) => (
                <MenuItem
                  key={c}
                  value={c}
                  sx={{ textTransform: "capitalize" }}
                >
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* From date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate")(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filters.endDate || undefined }}
            />
          </Grid>

          {/* To date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate")(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.startDate || undefined }}
            />
          </Grid>

          {/* Reset only — Search button removed */}
          <Grid item xs={12} sm={6} md="auto">
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              startIcon={<FilterAltOffIcon />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      {isError ? (
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={data?.data ?? []}
          page={data?.page ?? page}
          totalPages={data?.totalPages ?? 1}
          onPageChange={setPage}
          loading={isLoading || isCustomerLoading}
          actions={[]}
          getRowId={(row) => String(row._id)}
          emptyMessage="No transaction records found."
          tableLabel="RCS Usage History"
          itemsPerPage={limit}
          itemsPerPageOptions={LIMIT_OPTIONS}
          onItemsPerPageChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      )}

      {/* Export error snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack?.severity} onClose={() => setSnack(null)}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsageHistoryRcs;
